import React, { CSSProperties, PropsWithChildren, ReactElement } from 'react'
import styled from 'styled-components'
import { RADOM_COLORS } from '../util/Constants'
import Spinner from './Spinner'
import { Link } from 'react-router-dom'

export const HoverText = styled.div<{ textColor?: string, hoverColor?: string }>`
  cursor: pointer;
  ${({ textColor }) => `color: ${textColor ?? RADOM_COLORS.BLACK};`}

  :hover {  
    ${({ hoverColor }) => `color: ${hoverColor ?? RADOM_COLORS.GRAY_DARKEST};`}
  }
`

const PrimaryButtonInner = styled.button`
  background-color: var(--primary-btn-bg, #171717);
  border-radius: 12px;
  padding: 0 18px;
  outline: 0;
  border: 0;
  color: var(--primary-btn-ink, #ffffff);
  cursor: pointer;
  transition: 0.16s ease background-color, 0.16s ease opacity, 0.16s ease box-shadow, 0.12s ease transform;
  font-family: var(--font-sans);
  font-size: 13.75px;
  font-weight: 465;
  letter-spacing: -0.01em;
  line-height: 1.2;
  min-height: 42px;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;

  svg {
    transition: transform 0.16s ease, opacity 0.16s ease;
  }

  :hover {
    background-color: var(--primary-btn-bg-hover, #0f0f0f);
    box-shadow: 0 10px 24px rgba(17, 17, 17, 0.08);
  }
  
  :active {
    opacity: 0.98;
    transform: translateY(1px);
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.5;

    :active {
      color: var(--primary-btn-ink, #ffffff);
    }
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px var(--focus-ring-strong, rgba(17, 17, 17, 0.12));
  }
`

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean
  style?: CSSProperties
}

export const PrimaryButton = ({ disabled, ...props }: PropsWithChildren<ButtonProps>): ReactElement =>
  <PrimaryButtonInner style={props.style} disabled={props.isLoading || disabled} {...props}>
    {props.isLoading ? <Spinner style={{ color: 'var(--primary-btn-ink, #ffffff)' }} /> : props.children}
  </PrimaryButtonInner>

export const SecondaryButtonInner = styled.button`
  border: 1px solid;
  border-color: var(--border-strong, #d6dae2);
  border-radius: 12px;
  padding: 0 16px;
  outline: 0;
  background: var(--surface, #ffffff);
  color: ${RADOM_COLORS.BLACK};
  cursor: pointer;
  transition: 0.16s ease background-color, 0.16s ease border-color, 0.16s ease opacity, 0.16s ease box-shadow, 0.12s ease transform;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 455;
  letter-spacing: 0;
  line-height: 1.2;
  min-height: 40px;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  
  :hover {
    background: var(--surface-hover, #f7f7f8);
    border-color: var(--border-strong, #babfc8);
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }
  
  :active {
    opacity: 0.88;
    transform: translateY(1px);
  }

  :focus-visible {
    box-shadow: 0 0 0 4px var(--focus-ring, rgba(17, 17, 17, 0.08));
  }
`

export const SecondaryButton = ({ disabled, ...props }: PropsWithChildren<ButtonProps>): ReactElement =>
  <SecondaryButtonInner style={props.style} disabled={props.isLoading || disabled} {...props}>
    {props.isLoading ? <Spinner style={{ color: RADOM_COLORS.BLACK }} /> : props.children}
  </SecondaryButtonInner>

export const ButtonLeadingIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  transform: translateY(-0.5px);
`

export const SecondaryLinkButton = styled(Link)`
  border: 1px solid;
  border-color: var(--border-strong, #d6dae2);
  border-radius: 12px;
  padding: 0 16px;
  outline: 0;
  background: var(--surface, #ffffff);
  color: ${RADOM_COLORS.BLACK};
  cursor: pointer;
  transition: 0.18s ease background-color, 0.18s ease border-color, 0.18s ease opacity, 0.12s ease transform, 0.18s ease box-shadow;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 455;
  min-height: 40px;
  user-select: none;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  :hover {
    background: var(--surface-hover, #f7f7f8);
    border-color: var(--border-strong, #babfc8);
  }
  
  :active {
    opacity: 0.78;
  }

  :focus-visible {
    box-shadow: 0 0 0 4px var(--focus-ring, rgba(17, 17, 17, 0.08));
  }
`

export const PrimaryLinkButton = styled.a`
  background-color: var(--primary-btn-bg, #171717);
  border-radius: 12px;
  padding: 0 18px;
  outline: 0;
  border: 0;
  color: var(--primary-btn-ink, #ffffff);
  cursor: pointer;
  transition: 0.18s ease background-color, 0.18s ease opacity, 0.18s ease box-shadow, 0.12s ease transform;
  text-decoration: none;
  font-family: var(--font-sans);
  font-size: 13.75px;
  font-weight: 465;
  letter-spacing: -0.01em;
  line-height: 1.2;
  min-height: 42px;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    transition: transform 0.16s ease, opacity 0.16s ease;
  }
  
  :hover {
    background-color: var(--primary-btn-bg-hover, #0f0f0f);
    box-shadow: 0 10px 24px rgba(17, 17, 17, 0.08);
  }
  
  :active {
    opacity: 0.96;
    transform: translateY(1px);
  }

  :focus-visible {
    box-shadow: 0 0 0 4px var(--focus-ring-strong, rgba(17, 17, 17, 0.12));
  }
`

export const IconButton = styled.button`
  border: 0;
  border-radius: 12px;
  padding: 5px 7px;
  outline: 0;
  background: none;
  color: ${RADOM_COLORS.GRAY_DARKER};
  cursor: pointer;
  transition: 0.18s ease background-color, 0.18s ease opacity, 0.12s ease transform, 0.18s ease color;
  font-size: 11.5px;
  height: fit-content;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  
  :hover {
    background: var(--surface-hover, #f4f4f5);
    color: ${RADOM_COLORS.BLACK};
  }
  
  :active {
    opacity: 0.72;
    transform: translateY(1px);
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

export const IconLinkButton = styled(Link)`
  border: 0;
  border-radius: 12px;
  padding: 5px;
  outline: 0;
  background: none;
  color: ${RADOM_COLORS.GRAY_DARKER};
  cursor: pointer;
  transition: 0.18s ease background-color, 0.18s ease opacity, 0.12s ease transform, 0.18s ease color;
  font-size: 11.5px;
  height: fit-content;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  
  :hover {
    background: var(--surface-hover, #f4f4f5);
    color: ${RADOM_COLORS.BLACK};
  }
  
  :active {
    opacity: 0.72;
    transform: translateY(1px);
  }
`
